import { ActivatedRouteSnapshot, Router } from '@angular/router';

/**
 * Collects ActivatedRouteSnapshot objects from the root snapshot extracted through the routers state.
 * @param route The current ActivatedRouteSnapshot to collect from.
 * @param snapshots The array to collect snapshots into.
 */
export function collectRouteSnapshots(router: Router): ActivatedRouteSnapshot[];
/**
 * Collects ActivatedRouteSnapshot objects from the provided route snapshot.
 * @param route The current ActivatedRouteSnapshot to collect from.
 * @param snapshots The array to collect snapshots into.
 */
export function collectRouteSnapshots(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot[];
// Collects ActivatedRouteSnapshot objects.
export function collectRouteSnapshots(routeOrRouter: ActivatedRouteSnapshot | Router): ActivatedRouteSnapshot[] {
  let route: ActivatedRouteSnapshot;
  if (routeOrRouter instanceof Router) {
    route = routeOrRouter.routerState.snapshot.root;
  } else {
    route = routeOrRouter;
  }

  const snapshots: ActivatedRouteSnapshot[] = [];
  const parents: ActivatedRouteSnapshot[] = [route];

  while (parents.length > 0) {
    const parent = parents.shift();
    if (!parent) continue;

    snapshots.push(parent);
    parents.push(...parent.children);
  }

  return snapshots;
}

/**
 * Navigates to the current route with the specified paramater updated.
 * @param router The angular router service
 * @param paramKey The key of the paramater to change
 * @param paramValue The new value of the paramater
 */
export async function navigateToParam(router: Router, paramKey: string, paramValue: string) {
  const snapshots: ActivatedRouteSnapshot[] = collectRouteSnapshots(router);

  // Identify the activated route with the paramKey
  const activatedRoute = snapshots.find(snapshot => snapshot.paramMap.has(paramKey));
  if (!activatedRoute) return;

  // Identify the paramSegment to update by using the routeConfig path to identify what
  // segment has the matched paramKey. This feels a bit hacky.
  // Alternatively, we can use:
  //   const paramSegment = activatedRoute.url.find(segment => {
  //      return segment.path === activatedRoute.params[paramKey];
  //   });
  // But that can fail if multiple params in the same segment have the same value.
  // Example: A single route defined /foo/:param1/bar/:param2 and you wanted to updated
  // param2 to the value of 8. If the current route was /foo/4/bar/4, the param1
  // segment would match and you'd end up with /foo/8/bar/4.
  // The below implementation doesn't have that problem, but it might be brittle
  // in other ways.
  const configPath = activatedRoute.routeConfig?.path;
  const configPathSegments = configPath?.split('/') || [];
  const paramSegmentIndex = configPathSegments.indexOf(`:${paramKey}`);
  const paramSegment = activatedRoute.url[paramSegmentIndex];
  if (!paramSegment) return;

  // Build an array of all the segements
  const segments = snapshots.flatMap(s => s.url);

  // Buid up the navigation commands as the array of path segments.
  // In the case of the segment matching the paramSegment, use paramValue.
  const commands: string[] = [];
  for (const segment of segments) {
    const path = paramSegment === segment ? paramValue : segment.path;
    commands.push(path);
  }

  // TODO: handle query params

  // Navigate using the new commands
  await router.navigate(commands);
}
